'use client';

import type { ClassValue } from 'clsx';

import {
  Dialog,
  DialogOverlay,
  DialogPopup,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from '@vs-shop/ui/dialog';
import { IconTouchable } from '@vs-shop/ui/iconTouchable';
import { clsxm } from '@vs-shop/utils';
import { Menu } from 'lucide-react';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useTaxonTreeLazyLoad } from '../../../../hook/useTaxonTreeLazyLoad';
import { navigateFullPage } from '../../../../utils/router';

import MobileNavigationHeader from './MobileNavigationHeader';
import MobileNavigationLevel from './MobileNavigationLevel';
import MobileNavigationSpecialEventsLinks from './MobileNavigationSpecialEventsLinks';

import type { TaxonMenu } from '../../../../effect/schema/openapi/backend';

type NavigationStackLevel = {
  taxon: TaxonMenu | undefined;
  taxonChildren: readonly TaxonMenu[];
  title: string | null;
};

type Props = {
  taxons: readonly TaxonMenu[];
  className?: ClassValue;
};

export default function MobileNavigation({ taxons, className }: Props) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  // Where focus lands when the drawer opens. Stated rather than defaulted: the
  // primitive would otherwise focus the first tabbable inside — the close
  // button — for every non-touch opening, so a keyboard shopper would be told
  // "Zavřít menu, button" instead of being placed in the named dialog.
  const panelRef = useRef<HTMLDivElement>(null);
  const { fetchChildren, isLoading } = useTaxonTreeLazyLoad();

  const [stack, setStack] = useState<NavigationStackLevel[]>([
    {
      taxon: undefined,
      taxonChildren: taxons,
      title: null,
    },
  ]);

  const currentLevel = stack[stack.length - 1];

  const handleItemClick = async (taxon: TaxonMenu) => {
    // If taxon already has children from previous fetch, use them
    if (taxon.children !== undefined && taxon.children.length > 0) {
      const newLevel: NavigationStackLevel = {
        taxon: taxon,
        taxonChildren: taxon.children,
        title: taxon.name,
      };
      setStack((prev) => [...prev, newLevel]);
      return;
    }

    // Fetch children to see if this taxon has deeper levels
    const children = await fetchChildren(taxon.code);

    if (children.length > 0) {
      // Has children - expand into new level
      const newLevel: NavigationStackLevel = {
        taxon: { ...taxon, children },
        taxonChildren: children,
        title: taxon.name,
      };
      setStack((prev) => [...prev, newLevel]);
    } else {
      // No children - this is a leaf, navigate to page
      onOpenChange(false);
      navigateFullPage(taxon.routerUrl);
    }
  };

  const handleGoBack = () => {
    if (stack.length > 1) setStack((prev) => prev.slice(0, -1));
  };

  const onOpenChange = (open: boolean) => {
    setOpen(open);

    if (!open) {
      setStack([
        {
          taxon: undefined,
          taxonChildren: taxons,
          title: null,
        },
      ]);
    }
  };

  const currentLevelTitle = currentLevel?.title ?? null;
  const currentLevelTaxon = currentLevel?.taxon ?? null;
  const currentLevelTaxons = currentLevel?.taxonChildren ?? [];

  const handleViewAllClick = (taxonUrl: string) => {
    onOpenChange(false);
    navigateFullPage(taxonUrl);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={true}>
      <div className={clsxm(className)}>
        <DialogTrigger
          render={
            <IconTouchable
              data-slot="mobile-navigation-trigger"
              aria-label={intl.formatMessage({
                id: 'component.client.navigation.mobile.mobileNavigation.openMenu',
                defaultMessage: 'Otevřít menu',
              })}
            >
              <Menu className="size-7 shrink-0 text-foreground" />
            </IconTouchable>
          }
        />
      </div>
      <DialogPortal>
        <DialogOverlay data-slot="mobile-navigation-overlay" className="bg-background" />
        {/*
          The bare popup rather than `DialogContent`: this drawer is full-bleed,
          and the centred card would have to have every one of its decisions
          overridden to get back to an empty box.
        */}
        <DialogPopup
          ref={panelRef}
          initialFocus={panelRef}
          data-slot="mobile-navigation"
          className="fixed inset-0 z-(--z-max) flex flex-col gap-6 overflow-hidden bg-background px-4 pt-2 pb-4"
        >
          <DialogTitle className="sr-only">
            {intl.formatMessage({
              id: 'component.client.navigation.mobile.mobileNavigation.menuTitle',
              defaultMessage: 'Navigační menu',
            })}
          </DialogTitle>
          <header className="shrink-0">
            <MobileNavigationHeader
              title={currentLevelTitle}
              canGoBack={stack.length > 1}
              onGoBackAction={handleGoBack}
            />
          </header>
          <nav className="flex h-full flex-col overflow-x-hidden overflow-y-auto">
            <MobileNavigationLevel
              taxonUrl={currentLevelTaxon?.routerUrl ?? null}
              onViewAllClickAction={handleViewAllClick}
              taxonChildren={currentLevelTaxons}
              onTaxonChildClickAction={handleItemClick}
              isLoadingChildrenAction={isLoading}
              specialEventsLinks={
                stack.length === 1 ? (
                  <MobileNavigationSpecialEventsLinks
                    onClickAction={() => {
                      onOpenChange(false);
                    }}
                  />
                ) : null
              }
            />
          </nav>
        </DialogPopup>
      </DialogPortal>
    </Dialog>
  );
}
